import { useCallback, useRef } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';

import { expValEquals } from '@confluence/feature-experiments';
import { FORGE_MODULE_BYLINE } from '@confluence/forge-ui/entry-points/ForgeModuleType.ts';

type DynamicPropertiesResolutionOutcome = 'succeeded' | 'failed';
type ReportTitleAlignment = (
	titleChanged: boolean,
	resolutionOutcome: DynamicPropertiesResolutionOutcome,
) => void;

export const useBylineTitleAlignment = ({
	appId,
	contentId,
	hasAuthoritativeBylineAppSnapshot,
}: {
	appId: string;
	contentId?: string;
	hasAuthoritativeBylineAppSnapshot: boolean;
}): ReportTitleAlignment => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const reportedTitleAlignmentRef = useRef<{ appId: string; contentId?: string } | null>(null);
	return useCallback(
		(titleChanged: boolean, resolutionOutcome: DynamicPropertiesResolutionOutcome) => {
			if (
				!hasAuthoritativeBylineAppSnapshot ||
				!expValEquals('confluence_ssr_byline', 'isEnabled', true)
			) {
				return;
			}

			const reported = reportedTitleAlignmentRef.current;
			if (reported?.appId === appId && reported.contentId === contentId) {
				return;
			}

			reportedTitleAlignmentRef.current = { appId, contentId };
			createAnalyticsEvent({
				type: 'sendOperationalEvent',
				data: {
					action: 'resolved',
					actionSubject: 'forgeExtension',
					actionSubjectId: 'dynamicProperties',
					source: FORGE_MODULE_BYLINE,
					attributes: {
						resolutionOutcome,
						titleChanged,
					},
				},
			}).fire();
		},
		[appId, createAnalyticsEvent, contentId, hasAuthoritativeBylineAppSnapshot],
	);
};
